/**
 * The phone's bottom bar. Home comes first and Activity last; the areas between follow each person's order (Settings →
 * Navigation, contracts NavigationSettings: saved with their account, cached on the device by NavigationViewModel),
 * then the app's defaults (packages/core/config/navigation.json → NAVIGATION). Areas a workspace has off never show;
 * areas that are on but hidden or past the tab limit show in Home's top row.
 */
import type { FeatureArea, NavigationSettings, PhoneArea, WorkspaceFeatures } from "@bundle/contracts";
import { NAVIGATION } from "../generated/navigation.ts";
import { isFeatureEnabled } from "./features.ts";

/** A phone tab: Home, an area, or Activity. */
export type PhoneTab = "home" | PhoneArea | "activity";

export type PhoneTabs = {
  /** The bottom bar, in order. */
  tabs: PhoneTab[];
  /** Areas that are on but have no tab (hidden, or past the limit): Home's top row shows them, in order. */
  more: PhoneArea[];
};

/** How many areas fit between Home and Activity. */
export const PHONE_AREA_SLOTS = NAVIGATION.phone.maxTabs - 2;

/** The workspace switch behind each area; Files has none (always on, like the activity bar's Files view). */
export const PHONE_AREA_FEATURE: Readonly<Record<PhoneArea, FeatureArea | undefined>> = {
  mail: "mail",
  calendar: "calendar",
  contacts: "contacts",
  huddles: "huddles",
  docs: "docs",
  files: undefined,
};

/** Whether this workspace has `area` on (an area without a switch is always on). */
export function isPhoneAreaEnabled(features: WorkspaceFeatures | undefined, area: PhoneArea): boolean {
  const feature = PHONE_AREA_FEATURE[area];
  return feature === undefined || isFeatureEnabled(features, feature);
}

/** Every area in the person's order, then the app's for the areas they haven't placed (each once; unknown ones skipped). */
export function phoneAreaOrder(settings: NavigationSettings | undefined): PhoneArea[] {
  const known = NAVIGATION.phone.areas;
  const out: PhoneArea[] = [];
  for (const a of [...(settings?.phoneOrder ?? []), ...known]) if (known.includes(a) && !out.includes(a)) out.push(a);
  return out;
}

/** The bottom bar and Home's top row for this workspace's areas and my settings. */
export function phoneTabs(features: WorkspaceFeatures | undefined, settings: NavigationSettings | undefined): PhoneTabs {
  const on = phoneAreaOrder(settings).filter((a) => isPhoneAreaEnabled(features, a));
  const hidden = settings?.phoneHidden ?? [];
  const shown = on.filter((a) => !hidden.includes(a)).slice(0, PHONE_AREA_SLOTS);
  return { tabs: ["home", ...shown, "activity"], more: on.filter((a) => !shown.includes(a)) };
}

/** Moves an area to `index` in my order (the full order is saved, so later defaults can't reshuffle it). */
export function moveArea(settings: NavigationSettings, area: PhoneArea, index: number): NavigationSettings {
  const order = phoneAreaOrder(settings).filter((a) => a !== area);
  order.splice(Math.max(0, Math.min(index, order.length)), 0, area);
  return { ...settings, phoneOrder: order };
}

/** Areas this workspace has off, in my order (Settings → Navigation lists them so their place isn't a mystery). */
export function phoneAreasOff(features: WorkspaceFeatures | undefined, settings: NavigationSettings | undefined): PhoneArea[] {
  return phoneAreaOrder(settings).filter((a) => !isPhoneAreaEnabled(features, a));
}

/** Where Settings → Navigation puts an area: the bottom bar or Home's top row. */
export type PhoneSection = "bar" | "home";

/**
 * Puts an area at `index` of a section as this workspace shows it (Settings → Navigation's add, remove and drag). In the
 * bar it's shown, and a full bar's last tab moves to Home's top row; on Home it's hidden. Areas the workspace has off
 * keep their places.
 */
export function placeArea(
  settings: NavigationSettings,
  features: WorkspaceFeatures | undefined,
  area: PhoneArea,
  section: PhoneSection,
  index: number,
): NavigationSettings {
  const { tabs, more } = phoneTabs(features, settings);
  const bar = tabs.slice(1, -1).filter((a) => a !== area) as PhoneArea[];
  const home = more.filter((a) => a !== area);
  const list = section === "bar" ? bar : home;
  const at = Math.max(0, Math.min(index, section === "bar" ? Math.min(bar.length, PHONE_AREA_SLOTS - 1) : home.length));
  const order = phoneAreaOrder(settings).filter((a) => a !== area);
  // Before the row it lands on; at the end, after the section's last row (Home's top row follows the bar).
  const before = list[at];
  const after = section === "bar" ? bar[bar.length - 1] : (home[home.length - 1] ?? bar[bar.length - 1]);
  const pos = before !== undefined ? order.indexOf(before) : after !== undefined ? order.indexOf(after) + 1 : section === "bar" ? 0 : order.length;
  order.splice(pos, 0, area);
  return setAreaHidden({ ...settings, phoneOrder: order }, area, section === "home");
}

/** Whether I've changed my tabs (Settings → Navigation's Reset to default is off until I have). */
export function hasCustomTabs(settings: NavigationSettings | undefined): boolean {
  return settings?.phoneOrder !== undefined || settings?.phoneHidden !== undefined;
}

/** Takes an area off the bar (it shows in Home's top row) or puts it back. */
export function setAreaHidden(settings: NavigationSettings, area: PhoneArea, hidden: boolean): NavigationSettings {
  const rest = (settings.phoneHidden ?? []).filter((a) => a !== area);
  const next: NavigationSettings = { ...settings, phoneHidden: hidden ? [...rest, area] : rest };
  if (next.phoneHidden!.length === 0) delete next.phoneHidden;
  return next;
}

/**
 * Settings from the server or the device cache: arrays of strings, each area once. Areas this build doesn't know are
 * kept (a newer app may have saved them) and skipped by phoneTabs.
 */
export function parseNavigationSettings(raw: unknown): NavigationSettings {
  if (typeof raw !== "object" || raw === null) return {};
  const r = raw as { phoneOrder?: unknown; phoneHidden?: unknown };
  const out: NavigationSettings = {};
  const order = areaList(r.phoneOrder);
  const hidden = areaList(r.phoneHidden);
  if (order !== undefined) out.phoneOrder = order;
  if (hidden !== undefined) out.phoneHidden = hidden;
  return out;
}

/** Non-empty strings, each once; undefined when none. */
function areaList(v: unknown): PhoneArea[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: PhoneArea[] = [];
  for (const a of v) if (typeof a === "string" && a !== "" && !out.includes(a as PhoneArea)) out.push(a as PhoneArea);
  return out.length > 0 ? out : undefined;
}
